import type { IContext } from 'overmind';
import { derived } from 'overmind';
import {
  createStateHook,
  createActionsHook,
  createReactionHook,
  createEffectsHook
} from 'overmind-react';
import { merge, namespaced } from 'overmind/config';

import * as calculation from '@/features/execute-calculation-set/overmind/calculation';

import type { ApplicationConfiguration } from '../common/overmind/namespaces/arcgis/config/config.types';
import * as tenants from '../common/overmind/namespaces/tenants';
import * as actions from './actions';
import * as effects from './effects';
import type { AuthMachine, RouterMachine } from './machines';
import { createAuthMachine, createRouterMachine } from './machines';
import { arcgis } from './namespaces/arcgis';
import * as map from './namespaces/map';
import * as selectedFeature from './namespaces/selectedFeature';
import * as sidebar from './namespaces/sidebar';

type Config =
  | { type: 'NOT_AVAILABLE' }
  | { type: 'AVAILABLE'; config: ApplicationConfiguration };
type RootState = {
  auth: AuthMachine;
  router: RouterMachine;
  config: Config;
  isCalculationTacticLoaded: boolean;
  featureToggles?: FeatureToggles;
  hasConfigApiAccess: boolean;
};

export type FeatureToggles = {
  isEsriWebMapEnabled: boolean;
  isSharingExecutionEnabled: boolean;
  isSavingCalculationSetExecutionReportEnabled: boolean;
  isShowingGridElementTitleEnabled: boolean;
  isShowingApplicationVersionEnabled: boolean;
  isContainmentExecutionTimeCalculatedEnabled: boolean;
  isNewAuthVersionEnabled: boolean;
  isProtectiveDevicesEnabled: boolean;
  isWebmapBackgroundMapEnabled: boolean;
  isNewPropertiesForAcLineSegmentEnabled: boolean;
  isArcGisJsSDKWebMapForBrowsingCalculationResultsEnabled: boolean;
  shouldUseArcGisTokenRefreshing: boolean;
  shouldUseInitiatingObjectHighlighting: boolean;
  evaluationRulesTechnicalBoundariesAdjustment: boolean
};

const rootState: RootState = {
  auth: createAuthMachine(),
  router: createRouterMachine(),
  config: { type: 'NOT_AVAILABLE' },
  featureToggles: undefined,
  hasConfigApiAccess: false,
  isCalculationTacticLoaded: derived<Context['state'], RootState, any>(state =>
    state.calculation.calculationTactic.matches('LOADED')
  )
};

export const config = merge(
  {
    // root `state` needs to be inlined because referencing it from a module leads
    // to merging fail with HMR
    state: rootState,
    actions,
    effects
  },
  namespaced({
    calculation,
    arcgis,
    tenants,
    sidebar,
    selectedFeature,
    map
  })
);

export type Context = IContext<{
  state: typeof config.state;
  actions: typeof config.actions;
  effects: typeof config.effects;
}>;

export type State = {
  current: string;
};

export const useAppState = createStateHook<Context>();
export const useActions = createActionsHook<Context>();
export const useEffects = createEffectsHook<Context>();
export const useReaction = createReactionHook<Context>();
